import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Flex, Form, Select, Span } from "@ir/ui";
import { useAnalyticsFilter, useLookup, useWorkspace } from "@/hooks";
import {
  AnalyticsFilterInfo,
  ANALYTICS_DEFAULT_DATE_RANGE_IN_MONTHS,
  getDefaultAnalyticsFilterInfo,
  resolveAnalyticsFilterInfo,
  fetchAnalyticsScoreCardsAtom,
  fetchDeductionsByPurchaserAtom,
  fetchDeductionsByTypeAndReasonAtom,
  fetchDeductionVolumeVsGrossSalesAtom,
  fetchDeductionsByAgingBracketsAtom,
  fetchDisputePipelineAtom,
  fetchDeductionsByRetailerAtom,
} from "@/store/analytics";
import { DateService } from "@/core/services/Date.service";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { createLogger } from "@/infrastructure/services/logger";
import FilterDateRangeSelector from "@/presentation/deduction/components/DeductionFilter/DateRangeSelector";
import { FilterDateRange } from "@/presentation/deduction/components/DeductionFilter/interfaces";
import {
  useDateRangeHandlers,
  useDateRangeState,
  useDefaultFilterSync,
  useFormSync,
} from "@/presentation/deduction/components/DeductionFilter/hooks";
import { buildPurchaserOptions, DATE_CONSTANTS } from "@/presentation/deduction/components/DeductionFilter/utils";
import "./style/analyticsFilterBar.scss";
import { useAtom } from "jotai";

const dateService = new DateService(new DateAdapter());
const logger = createLogger("AnalyticsFilterBar");

const AnalyticsFilterBar = () => {
  const [form] = Form.useForm();
  const { settings, workspace } = useWorkspace();
  const { getPurchasersLookup, purchasersLookup } = useLookup();
  const { filterInfo, isFilterApplied, isHydrated, setFilter, clearFilter, setIsFilterApplied } = useAnalyticsFilter();
  const [, fetchAnalyticsScoreCards] = useAtom(fetchAnalyticsScoreCardsAtom);
  const [, fetchDeductionsByPurchaser] = useAtom(fetchDeductionsByPurchaserAtom);
  const [, fetchDeductionsByTypeAndReason] = useAtom(fetchDeductionsByTypeAndReasonAtom);
  const [, fetchDeductionVolumeVsGrossSales] = useAtom(fetchDeductionVolumeVsGrossSalesAtom);
  const [, fetchDeductionsByAgingBrackets] = useAtom(fetchDeductionsByAgingBracketsAtom);
  const [, fetchDisputePipeline] = useAtom(fetchDisputePipelineAtom);
  const [, fetchDeductionsByRetailer] = useAtom(fetchDeductionsByRetailerAtom);

  useEffect(() => {
    if (!workspace?.workspaceKey) return;
    getPurchasersLookup();
  }, [workspace?.workspaceKey, getPurchasersLookup]);

  const checkFromDateValue = Form.useWatch("checkFromDate", form);
  const checkToDateValue = Form.useWatch("checkToDate", form);

  const defaultFilterInfo = useMemo(() => getDefaultAnalyticsFilterInfo(), []);

  const checkFromDateMaxValue = checkToDateValue ?? DATE_CONSTANTS.checkFromDateDefaultMaxValue;
  const checkToDateMinValue =
    checkFromDateValue ?? defaultFilterInfo.checkFromDate ?? DATE_CONSTANTS.defaultFromDate;

  const purchaserOptions = useMemo(() => buildPurchaserOptions(purchasersLookup?.data ?? []), [purchasersLookup?.data]);

  const { checkDateRangeValue, updateCheckDateRange, updateInvoiceDateRange, resetToDefaults } = useDateRangeState({
    filterInfo,
    defaultCheckDateRange: defaultFilterInfo.checkDateRange,
    isFilterApplied,
  });

  const isDefaultFilter = useCallback(() => true, []);

  const { handleCheckDateRangeChange, calculateDateRange } = useDateRangeHandlers({
    defaultCheckDateRangeInMonths: ANALYTICS_DEFAULT_DATE_RANGE_IN_MONTHS,
    filterInfo,
    isFilterApplied,
    isDefaultFilter,
    form,
    onCheckDateRangeUpdate: updateCheckDateRange,
    onInvoiceDateRangeUpdate: updateInvoiceDateRange,
  });

  const skipDefaultSyncRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);

  const onCheckDateRangeChange = useCallback(
    (value: FilterDateRange | null) => {
      handleCheckDateRangeChange(value);
      skipDefaultSyncRef.current = true;
      setIsDirty(true);
    },
    [handleCheckDateRangeChange]
  );

  const onFieldsChange = (changedValues: Partial<AnalyticsFilterInfo>) => {
    setIsDirty(true);
    if (Object.hasOwn(changedValues, "checkFromDate") && checkDateRangeValue !== null) {
      form.setFieldsValue({ checkDateRange: null });
      updateCheckDateRange(null);
    }
    if (Object.hasOwn(changedValues, "checkToDate") && checkDateRangeValue !== null) {
      form.setFieldsValue({ checkDateRange: null });
      updateCheckDateRange(null);
    }
  };

  useFormSync({
    form,
    filterInfo,
    defaultFilterInfo,
    isFilterApplied,
    calculateDateRange,
  });

  useDefaultFilterSync({
    form,
    filterInfo,
    defaultFilterInfo,
    isFilterApplied,
    isHydrated,
    setFilter: filter => setFilter(filter as AnalyticsFilterInfo),
    skipNextSyncRef: skipDefaultSyncRef,
  });

  const handleSubmit = useCallback(
    async (values: AnalyticsFilterInfo) => {
      const formState = form.getFieldsValue();

      let checkFromDate =
        values.checkFromDate ??
        formState.checkFromDate ??
        filterInfo.checkFromDate ??
        defaultFilterInfo.checkFromDate;
      let checkToDate =
        values.checkToDate ?? formState.checkToDate ?? filterInfo.checkToDate ?? defaultFilterInfo.checkToDate;
      if (values.checkDateRange && (checkFromDate == null || checkToDate == null)) {
        const derived = calculateDateRange(values.checkDateRange, "checkDateRange");
        checkFromDate = checkFromDate ?? derived.fromDate;
        checkToDate = checkToDate ?? derived.toDate;
      }

      const next = resolveAnalyticsFilterInfo({
        ...filterInfo,
        ...values,
        checkFromDate,
        checkToDate,
        purchaser: values.purchaser ?? [],
      });

      logger.info("AnalyticsFilter submitted", { ...next });
      await setFilter(next);
      setIsFilterApplied(true);
      setIsDirty(false);

      fetchAnalyticsScoreCards();
      fetchDeductionsByPurchaser();
      fetchDeductionsByTypeAndReason();
      fetchDeductionVolumeVsGrossSales();
      fetchDeductionsByAgingBrackets();
      fetchDisputePipeline();
      fetchDeductionsByRetailer();
    },
    [
      form,
      filterInfo,
      calculateDateRange,
      setFilter,
      setIsFilterApplied,
      defaultFilterInfo.checkFromDate,
      defaultFilterInfo.checkToDate,
      fetchAnalyticsScoreCards,
      fetchDeductionsByPurchaser,
      fetchDeductionsByTypeAndReason,
      fetchDeductionVolumeVsGrossSales,
      fetchDeductionsByAgingBrackets,
      fetchDisputePipeline,
      fetchDeductionsByRetailer,
    ]
  );

  const handleClear = useCallback(async () => {
    form.resetFields();
    const defaults = getDefaultAnalyticsFilterInfo();
    form.setFieldsValue({
      checkDateRange: defaults.checkDateRange,
      checkFromDate: defaults.checkFromDate,
      checkToDate: defaults.checkToDate,
      purchaser: defaults.purchaser,
    });
    resetToDefaults(defaults.checkDateRange!);
    await clearFilter();
    setIsDirty(false);
    fetchAnalyticsScoreCards();
    fetchDeductionsByPurchaser();
    fetchDeductionsByTypeAndReason();
    fetchDeductionVolumeVsGrossSales();
    fetchDeductionsByAgingBrackets();
    fetchDisputePipeline();
    fetchDeductionsByRetailer();
  }, [
    form,
    resetToDefaults,
    clearFilter,
    fetchAnalyticsScoreCards,
    fetchDeductionsByPurchaser,
    fetchDeductionsByTypeAndReason,
    fetchDeductionVolumeVsGrossSales,
    fetchDeductionsByAgingBrackets,
    fetchDisputePipeline,
    fetchDeductionsByRetailer,
  ]);

  if (!isHydrated) return null;

  return (
    <Form
      form={form}
      className="analytics-filter-bar m-0 w-100"
      initialValues={defaultFilterInfo}
      onFinish={handleSubmit}
      onValuesChange={onFieldsChange}
    >
      <Flex align="center" gap={1} wrap="wrap" className="w-100">
        <Flex
          align="center"
          justifyContent="space-between"
          direction="row"
          wrap="no-wrap"
          gap={1}
        >
          <Span className="text-sm font-medium text-grey-700">Check Date Range:</Span>
          <Flex className="border-1 border-color-grey-200 rounded-small">
            <FilterDateRangeSelector
              button={{ size: "small", shape: null, color: "grey" }}
              value={checkDateRangeValue}
              fieldName="checkDateRange"
              onChange={onCheckDateRangeChange}
            />
          </Flex>
        </Flex>

        <Form.Item
          name="checkFromDate"
          dependencies={["checkToDate"]}
          layout={{ direction: "horizontal" }}
          className="m-0 form-item-no-label"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value && getFieldValue("checkToDate")) {
                  return Promise.reject(new Error("Start date is required"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker            
            minValue={DATE_CONSTANTS.checkFromDateDefaultMinValue}
            maxValue={checkFromDateMaxValue}
            dateFormat={settings?.displayDateFormat}
            shouldCloseOnInteractOutside={true}
            isNonModal={false}
          />
        </Form.Item>

        <Form.Item
          name="checkToDate"
          dependencies={["checkFromDate"]}
          layout={{ direction: "horizontal" }}
          className="m-0 form-item-no-label"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value && getFieldValue("checkFromDate")) {
                  return Promise.reject(new Error("End date is required"));
                }
                const fromDate = getFieldValue("checkFromDate");
                if (value && fromDate && dateService.isBefore(value, fromDate)) {
                  return Promise.reject(new Error("End date cannot be before start date"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker
            minValue={checkToDateMinValue}
            maxValue={DATE_CONSTANTS.checkFromDateDefaultMaxValue}
            dateFormat={settings?.displayDateFormat}
            shouldCloseOnInteractOutside={true}
            isNonModal={false}
          />
        </Form.Item>

        <Form.Item
          label="Purchaser"
          name="purchaser"
          className="m-0 form-item-purchaser"
          isList={true}
          layout={{ direction: "horizontal" }}
        >
          <Select
            size="small"
            maxMenuHeight={200}
            options={purchaserOptions}
            isMultiSelect={true}
            isClearable={true}
            isLoading={purchasersLookup?.isLoading}
            defaultValue={filterInfo.purchaser}
            placeholder="All Purchasers"
            maxTagCount={2}
            maxTagLength={6}
          />
        </Form.Item>
        <Button type="submit" variant="solid" color="primary" size="small" htmlTitle="Submit" isDisabled={!isDirty}>
          Submit
        </Button>
        {isFilterApplied && (
          <Button
            type="reset"
            variant="outline"
            color="grey"
            size="small"
            onPress={handleClear}
            htmlTitle="Clear filters"
            className="m-0"
          >
            Clear
          </Button>
        )}
      </Flex>
    </Form>
  );
};

export default AnalyticsFilterBar;
